import styled from "styled-components";

const ConsentItemsContainer = styled.div`
  background-color: #f7efdd;
  color: ${({ theme }) => theme.consentItems.textColor};
  border-radius: 4px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 160px;
  position: relative;
  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 64px;
    background: linear-gradient(to bottom, #f7efdd00 0%, #f7efdd 100%);
    pointer-events: none;
    z-index: 1;
  }
`;

const ConsentItemsScroll = styled.div<{ $isDesktop: boolean }>`
  display: flex;
  flex-direction: column;
  max-height: 160px;
  overflow: auto;
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-track-piece {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #371f13;
    border-radius: 999px;
  }

  &::-webkit-scrollbar-button {
    display: none;
    width: 0;
    height: 0;
  }

  &::-webkit-scrollbar-corner {
    background: transparent;
  }

  &::-webkit-resizer {
    background: transparent;
  }
`;
export const ConsentItem = styled.span`
  text-align: left;
  margin-bottom: 9px;
  font-size: 1.4rem;
  line-height: 1.8rem;
  font-weight: 500;
`;

export const CaptionRow = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  text-align: left;
`;

export const CaptionIcon = styled.span`
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

export const CaptionText = styled.span`
  font-size: 1.4rem;
  line-height: 1.8rem;
  color: #55544c;
  font-weight: 400;
`;

const Text = styled.p`
  font-size: 1.4rem;
  line-height: 1.8rem;
  text-align: center;
  margin: 0;
  color: #55544c;
  font-weight: 400;
`;

const ItemsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

export interface ConsentItemsProps {
  confirmationText: string;
  toShareAttributes?: Array<string> | undefined;
  disclaimerText: string;
  onlyOpenIdScopeText: string;
  isDesktop: boolean;
  showConfirmationText?: boolean;
}

export function ConsentItems(props: ConsentItemsProps): JSX.Element {
  const showConfirmationText = props.showConfirmationText ?? true;
  return props.toShareAttributes?.length !== 0 ? (
    <ItemsBlock>
      {showConfirmationText && (
        <CaptionRow data-testid="consent_confirmation_text">
          <CaptionIcon aria-hidden="true">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="24" height="24" rx="8" fill="#371F13" />
              <path
                d="M16.9007 14.7281C16.911 14.8303 16.9161 14.9606 16.9161 15.119C16.9161 15.2722 16.9059 15.4511 16.8854 15.6554C16.8701 15.8547 16.8497 16.0488 16.8241 16.2378C16.7577 16.667 16.6913 16.9735 16.6249 17.1575C15.5315 17.7041 14.1112 17.9774 12.364 17.9774C10.7291 17.9774 9.33437 17.5457 8.17974 16.6823C7.33676 16.0539 6.74667 15.1726 6.40948 14.0384C6.23578 13.456 6.14893 12.7484 6.14893 11.9157C6.14893 11.0778 6.30986 10.2884 6.63172 9.54765C6.95359 8.80174 7.40318 8.16823 7.98049 7.64711C9.1811 6.56401 10.7393 6.02246 12.6552 6.02246C13.0128 6.02246 13.4803 6.05567 14.0576 6.12209C14.6349 6.1885 15.013 6.22937 15.1918 6.2447L15.7129 6.29835C16.1063 6.33411 16.3643 6.35199 16.4869 6.35199H16.7322C16.7679 6.35199 16.8241 6.34688 16.9007 6.33666C16.911 6.45928 16.9161 6.74793 16.9161 7.20263C16.9161 7.65733 16.8343 8.42112 16.6708 9.494H15.6899C15.5929 8.72255 15.4702 8.18866 15.3221 7.89234C14.898 7.21285 13.9708 6.8731 12.5402 6.8731C11.1097 6.8731 9.99342 7.34313 9.19132 8.28318C8.42497 9.17725 8.0418 10.36 8.0418 11.8314C8.0418 13.5326 8.52204 14.8635 9.48253 15.824C10.3613 16.7028 11.4342 17.1421 12.7012 17.1421C13.6157 17.1421 14.3105 16.9684 14.7856 16.621C15.2659 16.2685 15.6516 15.6375 15.9428 14.7281H16.9007Z"
                fill="#FAF5EB"
              />
            </svg>
          </CaptionIcon>
          <CaptionText>{props.confirmationText}</CaptionText>
        </CaptionRow>
      )}
      {!!props.toShareAttributes ? (
        <ConsentItemsContainer data-testid="consent_items">
          <ConsentItemsScroll $isDesktop={props.isDesktop}>
            {props.toShareAttributes.map((val, i) => {
              return (
                <ConsentItem key={i} data-testid={"consent_item_" + val}>
                  {val}
                </ConsentItem>
              );
            })}
          </ConsentItemsScroll>
        </ConsentItemsContainer>
      ) : (
        <p></p>
      )}
      <Text data-testid="consent_items_disclaimer">{props.disclaimerText}</Text>
    </ItemsBlock>
  ) : (
    <Text>{props.onlyOpenIdScopeText}</Text>
  );
}
